import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import {
  ALARM_ACTIVE_LINE_COLOR,
  EVENT_ACTIVE_LINE_COLOR,
  IS_ALARM_LAYER,
  IS_ECO_DRIVE_LAYER,
  IS_EVENT_LAYER,
  IS_RIDE_LAYER,
  IS_START_POSITION,
  IS_STOP_POSITION,
  RIDE_DETAIL_ZOOM_LEVEL,
} from '../constants';
import {
  IRide,
  IRideMapMarkerTooltips,
  IRideMapMarkers,
  IRideMapStopMarkers,
  IRidePlayerSpeedLimitsSettings,
  IRidePosition,
  TRideActiveLayer,
} from '../types';
import {
  activeRideNumberAtom,
  enableSpeedlimitsAtom,
  selectedRidesAtom,
  speedlimitSettingsAtom,
} from '../RidePlayerState';
import {
  createPositionMarkers,
  createPositionTooltips,
  createStartMarker,
  createStopMarker,
  hideAllTooltips,
  updatePositionMarkersWithNewPosition,
} from '../RidePlayerMarkerService';
import {
  positionMarkerIcon,
  stopMarkerIcon,
  stopMarkerIconDisabled,
} from '../RidePlayerIconService';

interface IUsePositionMarker {
  positionMarkers: IRideMapMarkers;
  positionMarkerTooltips: IRideMapMarkerTooltips;
  showHiddenPositionMarkers: () => void;
  hideShowedPositionMarkers: () => void;
  handleToggleTooltip: (rideNumber: number, positionIndex: number) => void;
  showAllPositionMarkers: (exceptRideNumber?: number) => void;
  hideAllPositionMarkers: (exceptRideNumber?: number) => void;
  toggleAllPositionMarkerTooltips: (toggleValue: boolean) => void;
  showAllStopMarkers: () => void;
  hideAllStopMarkers: () => void;
  setAllPositionMarkersColor: (color: string, exceptRideNumber?: number) => void;
  resetPositionMarkersColorForRide: (rideNumber: number) => void;
  resetAllPositionMarkersColor: () => void;
  setStopMarkerEnabled: (rideNumber: number) => void;
  setAllStopMarkersDisabled: (exceptRideNumber?: number) => void;
  setAllStopMarkersEnabled: (exceptRideNumber?: number) => void;
  setStopMarkerZIndexForRide: (rideNumber: number, zIndex: number) => void;
  resetAllStopMarkersZIndex: () => void;
  changeAllPositionMarkersToAlarm: () => void;
  changeAllPositionMarkersToEvent: () => void;
  changeAllPositionMarkersToEcoDrive: () => void;
  changeAllPositionMarkersToRide: () => void;
  setAllPositionMarkersZIndex: (zIndex: number, exceptRideNumber?: number) => void;
  hideAllPositionMarkersExceptRide: (rideNumber: number) => void;
  showAllPositionMarkersForRide: (rideNumber: number) => void;
  removeAllPositionMarkers: () => void;
}

/**
 * useRidePlayerPositionMarker custom hook
 * @param {google.maps.Map} map
 * @param {IRide[]} rides
 * @param {number} zoomLevel
 * @param {TRideActiveLayer} activeLayer
 * @param {boolean} isLastRidePlayer
 * @param {IRidePosition|null} actualRidePosition
 * @returns {IUsePositionMarker}
 */
const usePositionMarker = (
  map: google.maps.Map,
  rides: IRide[],
  zoomLevel: number,
  activeLayer: TRideActiveLayer,
  isLastRidePlayer?: boolean,
  actualRidePosition?: IRidePosition | null
): IUsePositionMarker => {
  const intl = useIntl();
  const positionMarkers = React.useRef<IRideMapMarkers>({ 1: [] });
  const positionMarkerTooltips = React.useRef<IRideMapMarkerTooltips>({ 1: [] });
  const enableSpeedlimits = useRecoilValue<boolean>(enableSpeedlimitsAtom);
  const speedlimitSettings = useRecoilValue<IRidePlayerSpeedLimitsSettings>(speedlimitSettingsAtom);
  const startPositionMarker = React.useRef<google.maps.Marker | null>(null);
  const selectedRides = useRecoilValue<number[]>(selectedRidesAtom);
  const activeRide = useRecoilValue<number>(activeRideNumberAtom);
  const stopMarkers = React.useRef<IRideMapStopMarkers>({ 1: null });

  /**
   * Show hidden position markers
   * @returns {void}
   */
  const showHiddenPositionMarkers = (): void => {
    if (positionMarkers.current) {
      positionMarkers.current[activeRide].forEach((marker: google.maps.Marker) => {
        if (!marker.getMap()) {
          marker.setMap(map);
        }
      });
    }
  };

  /**
   * Hide showed position markers
   * @returns {void}
   */
  const hideShowedPositionMarkers = (): void => {
    if (positionMarkers.current && positionMarkers.current[activeRide]) {
      positionMarkers.current[activeRide].forEach((marker: google.maps.Marker) => {
        if (
          marker.getMap() &&
          (marker.getIcon() as google.maps.Icon).url.search('RidePositionMarker')
        ) {
          marker.setMap(null);
        }
      });
    }
  };

  /**
   * Toggle marker tooltip handler
   * @param {number} rideNumber
   * @param {number{ positionIndex
   * @returns {void}
   */
  const handleToggleTooltip = (rideNumber: number, positionIndex: number): void => {
    if (
      positionMarkerTooltips.current[rideNumber] &&
      positionMarkerTooltips.current[rideNumber][positionIndex]
    ) {
      const currentMarkerTooltip = positionMarkerTooltips.current[rideNumber][positionIndex];
      hideAllTooltips(positionMarkerTooltips, positionIndex);
      if (currentMarkerTooltip) {
        currentMarkerTooltip.setMap(currentMarkerTooltip.getMap() ? null : map);
      }
    }
  };

  /**
   * Show/hide all position marker tooltips
   * @param {boolean} toggleValue
   * @returns void
   */
  const toggleAllPositionMarkerTooltips = (toggleValue: boolean): void => {
    Object.keys(positionMarkerTooltips.current).forEach((rideNumber: string) => {
      Object.keys(positionMarkerTooltips.current[Number(rideNumber)]).forEach(
        (positionIndex: string) => {
          const markerTooltip =
            positionMarkerTooltips.current[Number(rideNumber)][Number(positionIndex)];
          if (markerTooltip && toggleValue && !markerTooltip.getMap()) {
            markerTooltip.setMap(map);
          }
          if (markerTooltip && !toggleValue && markerTooltip.getMap()) {
            markerTooltip.setMap(null);
          }
        }
      );
    });
  };

  /**
   * Show all position markers
   * @params {number} exceptRideNumber
   * @returns {void}
   */
  const showAllPositionMarkers = (exceptRideNumber?: number): void => {
    Object.keys(positionMarkers.current).forEach((rideNumber: string) => {
      if (exceptRideNumber && exceptRideNumber === Number(rideNumber)) {
        return;
      }
      positionMarkers.current[Number(rideNumber)].forEach((positionMarker: google.maps.Marker) => {
        if (
          positionMarker &&
          !positionMarker.getMap() &&
          selectedRides.includes(Number(rideNumber))
        ) {
          positionMarker.setMap(map);
        }
      });
    });
  };

  /**
   * Show all stop markers
   * @returns {void}
   */
  const showAllStopMarkers = (): void => {
    Object.keys(stopMarkers.current).forEach((rideNumber: string) => {
      const stopMarker = stopMarkers.current[Number(rideNumber)];
      if (stopMarker && selectedRides.includes(Number(rideNumber))) {
        stopMarker.setMap(map);
      }
    });
  };

  /**
   * Hide all stop markers
   * @returns {void}
   */
  const hideAllStopMarkers = (): void => {
    Object.keys(stopMarkers.current).forEach((rideNumber: string) => {
      const stopMarker = stopMarkers.current[Number(rideNumber)];
      // if (stopMarker && stopMarker.getMap()) {
      if (stopMarker) {
        stopMarker.setMap(null);
      }
    });
  };

  /**
   * Hide all position markers
   * @param {number} exceptRideNumber
   * @returns {void}
   */
  const hideAllPositionMarkers = (exceptRideNumber?: number): void => {
    Object.keys(positionMarkers.current).forEach((rideNumber: string) => {
      if (exceptRideNumber && exceptRideNumber === Number(rideNumber)) {
        return;
      }
      positionMarkers.current[Number(rideNumber)].forEach((positionMarker: google.maps.Marker) => {
        if (positionMarker && selectedRides.includes(Number(rideNumber))) {
          positionMarker.setMap(null);
        }
      });
    });
  };

  /**
   * Unset position markers, tooltips and start position marker from map
   */
  const unsetMarkersFromMap = () => {
    // hideAllPositionMarkers();
    positionMarkers.current = { 1: [] };
    toggleAllPositionMarkerTooltips(false);
    positionMarkerTooltips.current = { 1: {} };
    if (startPositionMarker.current) {
      startPositionMarker.current.setMap(null);
    }
    hideAllStopMarkers();
    startPositionMarker.current = null;
    stopMarkers.current = { 1: null };
  };

  /**
   * Set all position markers color
   * @param {string} color
   * @param {number} exceptRideNumber
   * @returns {void}
   */
  const setAllPositionMarkersColor = (color: string, exceptRideNumber?: number): void => {
    if (positionMarkers.current) {
      Object.keys(positionMarkers.current).forEach((rideNumber: string) => {
        if (exceptRideNumber && exceptRideNumber === Number(rideNumber)) {
          return;
        }
        const markers: google.maps.Marker[] = positionMarkers.current[Number(rideNumber)];
        markers.forEach((marker: google.maps.Marker) => {
          const newIcon: google.maps.Icon = {
            url: positionMarkerIcon(color),
            anchor: new google.maps.Point(6, 6),
          };
          marker.setIcon(newIcon);
        });
      });
    }
  };

  /**
   * Reset position markers to its original color for specified ride
   * @param {number} rideNumber
   * @returns {void}
   */
  const resetPositionMarkersColorForRide = (rideNumber: number): void => {
    if (positionMarkers.current[rideNumber]) {
      positionMarkers.current[rideNumber].forEach((marker: google.maps.Marker) => {
        let iconColor = enableSpeedlimits
          ? marker.get('originalColorSpeedlimits')
          : marker.get('originalColor');
        if (activeLayer === IS_ALARM_LAYER) {
          iconColor = ALARM_ACTIVE_LINE_COLOR;
        }
        if (activeLayer === IS_ECO_DRIVE_LAYER) {
          iconColor = marker.get('originalColorEcoScore');
        }
        if (activeLayer === IS_EVENT_LAYER) {
          iconColor = EVENT_ACTIVE_LINE_COLOR;
        }

        const newIcon: google.maps.Icon = {
          url: positionMarkerIcon(iconColor || '#146755'),
          anchor: new google.maps.Point(6, 6),
        };
        marker.setIcon(newIcon);
      });
    }
  };

  /**
   * Reset all position markers color to original
   * @returns {void}
   */
  const resetAllPositionMarkersColor = (): void => {
    Object.keys(positionMarkers.current).forEach((rideNumber: string) => {
      positionMarkers.current[Number(rideNumber)].forEach((marker: google.maps.Marker | null) => {
        if (marker) {
          const newIcon: google.maps.Icon = {
            url: positionMarkerIcon(
              enableSpeedlimits
                ? marker.get('originalColorSpeedlimits')
                : marker.get('originalColor')
            ),
            anchor: new google.maps.Point(6, 6),
          };
          marker.setIcon(newIcon);
        }
      });
    });
  };

  const changeAllPositionMarkersToAlarm = () => {
    setAllPositionMarkersColor(ALARM_ACTIVE_LINE_COLOR);
  };

  const changeAllPositionMarkersToEvent = () => {
    setAllPositionMarkersColor(EVENT_ACTIVE_LINE_COLOR);
  };

  const changeAllPositionMarkersToEcoDrive = () => {
    Object.keys(positionMarkers.current).forEach((rideNumber: string) => {
      positionMarkers.current[Number(rideNumber)].forEach((marker: google.maps.Marker | null) => {
        if (marker) {
          let ecoScoreColor = marker.get('originalColorEcoScore');
          if (!ecoScoreColor) {
            console.warn('EcoScore color for position is null. Using default good color.');
            ecoScoreColor = '#146755';
          }
          const newIcon: google.maps.Icon = {
            url: positionMarkerIcon(ecoScoreColor),
            anchor: new google.maps.Point(6, 6),
          };
          marker.setIcon(newIcon);
        }
      });
    });
  };

  const changeAllPositionMarkersToRide = () => {
    Object.keys(positionMarkers.current).forEach((rideNumber: string) => {
      positionMarkers.current[Number(rideNumber)].forEach((marker: google.maps.Marker | null) => {
        if (marker) {
          const newIcon: google.maps.Icon = {
            url: positionMarkerIcon(
              enableSpeedlimits
                ? marker.get('originalColorSpeedlimits')
                : marker.get('originalColor')
            ),
            anchor: new google.maps.Point(6, 6),
          };
          marker.setIcon(newIcon);
        }
      });
    });
  };

  /**
   * Set all stop markers to disabled
   * @param {number} exceptRideNumber
   * @returns {void}
   */
  const setAllStopMarkersDisabled = (exceptRideNumber?: number): void => {
    if (stopMarkers.current) {
      Object.keys(stopMarkers.current).forEach((rideNumber: string) => {
        if (exceptRideNumber && exceptRideNumber === Number(rideNumber)) {
          return;
        }
        const marker = stopMarkers.current[Number(rideNumber)] as google.maps.Marker;
        if (marker) {
          const newIcon: google.maps.Icon = {
            url: stopMarkerIconDisabled(),
            anchor: new google.maps.Point(10, 10),
          };
          marker.setIcon(newIcon);
        }
      });
    }
  };

  /**
   * Set all stop markers to enabled
   * @param {number} exceptRideNumber
   * @returns {void}
   */
  const setAllStopMarkersEnabled = (exceptRideNumber?: number): void => {
    if (stopMarkers.current) {
      Object.keys(stopMarkers.current).forEach((rideNumber: string) => {
        if (exceptRideNumber && exceptRideNumber === Number(rideNumber)) {
          return;
        }
        const marker = stopMarkers.current[Number(rideNumber)] as google.maps.Marker;
        if (marker) {
          const newIcon: google.maps.Icon = {
            url: stopMarkerIcon(),
            anchor: new google.maps.Point(10, 10),
          };
          marker.setIcon(newIcon);
        }
      });
    }
  };

  /**
   * Set all stop markers to enabled
   * @param {number} zIndex
   * @param {number} exceptRideNumber
   * @returns {void}
   */
  const setAllPositionMarkersZIndex = (zIndex: number, exceptRideNumber?: number): void => {
    if (positionMarkers.current) {
      Object.keys(positionMarkers.current).forEach((rideNumber: string) => {
        if (exceptRideNumber && exceptRideNumber === Number(rideNumber)) {
          return;
        }
        positionMarkers.current[Number(rideNumber)].forEach((marker: google.maps.Marker | null) => {
          if (marker) {
            marker.setZIndex(zIndex);
          }
        });
      });
    }
  };

  const hideAllPositionMarkersExceptRide = (rideNumber: number) => {
    if (positionMarkers.current) {
      Object.keys(positionMarkers.current).forEach((rideNum: string) => {
        if (rideNumber === Number(rideNum)) {
          return;
        }
        positionMarkers.current[rideNumber].forEach((marker: google.maps.Marker | null) => {
          if (marker && marker.getMap()) {
            marker.setMap(null);
          }
        });
      });
    }
  };

  /**
   * Show position markers for ride
   * @param {number} rideNumber - Ride number
   * @returns {void}
   */
  const showAllPositionMarkersForRide = (rideNumber: number) => {
    if (
      positionMarkers.current &&
      positionMarkers.current[rideNumber] &&
      selectedRides.includes(rideNumber)
    ) {
      positionMarkers.current[rideNumber].forEach((marker: google.maps.Marker | null) => {
        if (marker && !marker.getMap()) {
          marker.setMap(map);
        }
      });
    }
  };

  /**
   * Set stop marker to enabled for specified ride
   * @param {number} rideNumber
   * @returns {void}
   */
  const setStopMarkerEnabled = (rideNumber: number): void => {
    if (stopMarkers.current) {
      const marker = stopMarkers.current[rideNumber] as google.maps.Marker;
      if (marker) {
        const newIcon: google.maps.Icon = {
          url: stopMarkerIcon(),
          anchor: new google.maps.Point(10, 10),
        };
        marker.setIcon(newIcon);
      }
    }
  };

  /**
   * Reset all stop markers zindexes to its original values
   * @returns {void}
   */
  const resetAllStopMarkersZIndex = (): void => {
    if (stopMarkers.current) {
      Object.keys(stopMarkers.current).forEach((rideNumber: string) => {
        const marker: google.maps.Marker | null = stopMarkers.current[Number(rideNumber)];
        if (marker) {
          marker.setZIndex(marker.get('originalZIndex'));
        }
      });
    }
  };

  /**
   * Set zIndex for stop marker for specified ride
   * @param {number} rideNumber
   * @param {number} zIndex
   * @returns {void}
   */
  const setStopMarkerZIndexForRide = (rideNumber: number, zIndex: number): void => {
    if (stopMarkers.current) {
      const marker = stopMarkers.current[rideNumber] as google.maps.Marker;
      if (marker) {
        marker.setZIndex(zIndex);
      }
    }
  };

  const removeAllPositionMarkers = () => {
    stopMarkers.current = {};
    positionMarkers.current = {};
    startPositionMarker.current = null;
  };

  // Handle show/hide position markers and stop markers by selected rides
  React.useEffect(() => {
    Object.keys(positionMarkers.current).forEach((rideNumber: string) => {
      positionMarkers.current[Number(rideNumber)].forEach((marker: google.maps.Marker) => {
        if (!selectedRides.includes(Number(rideNumber)) && marker.getMap()) {
          marker.setMap(null);
        }
        if (
          selectedRides.includes(Number(rideNumber)) &&
          !marker.getMap() &&
          activeLayer === IS_RIDE_LAYER &&
          zoomLevel >= RIDE_DETAIL_ZOOM_LEVEL &&
          Number(rideNumber) === activeRide
        ) {
          marker.setMap(map);
        }
      });
    });
    if (stopMarkers.current) {
      Object.keys(stopMarkers.current).forEach((rideNumber: string) => {
        const stopMarker = stopMarkers.current[Number(rideNumber)];
        if (stopMarker && !selectedRides.includes(Number(rideNumber))) {
          stopMarker.setMap(null);
        }
        if (stopMarker && selectedRides.includes(Number(rideNumber))) {
          stopMarker.setMap(map);
        }
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLayer, map, selectedRides]);

  React.useEffect(() => {
    // Create start and stop markers only when one ride is present
    if (rides.length === 1) {
      // Create start marker
      if (
        rides[activeRide - 1].positions[0] &&
        rides[activeRide - 1].positions[0].type === IS_START_POSITION
      ) {
        if (rides.length === 1) {
          startPositionMarker.current = createStartMarker(
            map,
            new google.maps.LatLng(
              rides[activeRide - 1].positions[0].gpsLat,
              rides[activeRide - 1].positions[0].gpsLon
            ),
            {
              rideNumber: activeRide,
              positionIndex: 0,
              toggleTooltipCallback: handleToggleTooltip,
            }
          );
        }
      }
      // Create stop marker
      const { positions } = rides[activeRide - 1];
      if (
        !isLastRidePlayer &&
        positions[positions.length - 1] &&
        positions[positions.length - 1].type === IS_STOP_POSITION
      ) {
        stopMarkers.current[activeRide] = createStopMarker(
          map,
          new google.maps.LatLng(
            positions[positions.length - 1].gpsLat,
            positions[positions.length - 1].gpsLon
          ),
          {
            rideNumber: activeRide,
            positionIndex: positions.length - 1,
            toggleTooltipCallback: handleToggleTooltip,
          }
        );
      }
    }
    // Create stop markers for more rides
    if (rides.length > 1) {
      rides.forEach((ride: IRide, index: number) => {
        // Create stop marker
        const { positions } = rides[index];
        if (
          !isLastRidePlayer &&
          positions[positions.length - 1] &&
          positions[positions.length - 1].type === IS_STOP_POSITION &&
          stopMarkers.current
        ) {
          stopMarkers.current[ride.rideData.key] = createStopMarker(
            map,
            new google.maps.LatLng(
              positions[positions.length - 1].gpsLat,
              positions[positions.length - 1].gpsLon
            ),
            {
              rideNumber: ride.rideData.key,
              positionIndex: positions.length - 1,
              toggleTooltipCallback: handleToggleTooltip,
            }
          );
        }
      });
    }
    // Create markers for positions
    positionMarkers.current = createPositionMarkers(
      zoomLevel >= RIDE_DETAIL_ZOOM_LEVEL ? map : undefined,
      rides,
      enableSpeedlimits,
      speedlimitSettings,
      handleToggleTooltip
    );
    // Create tooltips for position markers
    positionMarkerTooltips.current = createPositionTooltips(rides, intl);

    return () => {
      Object.keys(positionMarkers.current).forEach((rideNumber: string) => {
        positionMarkers.current[Number(rideNumber)].forEach(
          (positionMarker: google.maps.Marker) => {
            if (positionMarker) {
              positionMarker.setMap(null);
            }
          }
        );
      });
      unsetMarkersFromMap();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speedlimitSettings]);

  // Update position markers by speedlimit color when speedlimit settings changes
  // React.useEffect(() => {
  //   if (activeLayer === IS_RIDE_LAYER) {
  //     console.log('useEffect - updateAllPositionMarkersColorBySpeedlimits');
  //     RidePlayerMarkerService.updateAllPositionMarkersColorBySpeedlimits(
  //       positionMarkers.current,
  //       enableSpeedlimits,
  //       speedlimitSettings,
  //       isRideActive
  //     );
  //   }
  // }, [activeLayer, enableSpeedlimits, isRideActive, speedlimitSettings]);

  // If actual position is present - updated markers
  React.useEffect(() => {
    if (actualRidePosition && activeRide) {
      const positionIndex = rides[activeRide - 1].positions.length - 1;
      const rideData = rides[activeRide - 1].rideData;
      updatePositionMarkersWithNewPosition(
        zoomLevel >= RIDE_DETAIL_ZOOM_LEVEL ? map : undefined,
        positionMarkers.current,
        positionMarkerTooltips.current,
        activeRide,
        rideData,
        actualRidePosition,
        enableSpeedlimits,
        speedlimitSettings,
        positionIndex,
        intl,
        handleToggleTooltip
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualRidePosition, activeRide, map]);

  const data: IUsePositionMarker = {
    positionMarkers: positionMarkers.current,
    positionMarkerTooltips: positionMarkerTooltips.current,
    showHiddenPositionMarkers,
    hideShowedPositionMarkers,
    handleToggleTooltip,
    showAllPositionMarkers,
    toggleAllPositionMarkerTooltips,
    hideAllPositionMarkers,
    hideAllStopMarkers,
    showAllStopMarkers,
    setAllPositionMarkersColor,
    resetPositionMarkersColorForRide,
    resetAllPositionMarkersColor,
    setStopMarkerEnabled,
    setAllStopMarkersDisabled,
    setAllStopMarkersEnabled,
    resetAllStopMarkersZIndex,
    setStopMarkerZIndexForRide,
    changeAllPositionMarkersToAlarm,
    changeAllPositionMarkersToEvent,
    changeAllPositionMarkersToEcoDrive,
    changeAllPositionMarkersToRide,
    setAllPositionMarkersZIndex,
    hideAllPositionMarkersExceptRide,
    showAllPositionMarkersForRide,
    removeAllPositionMarkers,
  };

  return data;
};

export default usePositionMarker;
